import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/lib/store";
import { api, media } from "@/lib/api";
import { DEFAULT_CHROME, mergeChrome } from "@/lib/chromeContent";
import { ShoppingCart, Search, Menu, X, FlaskConical, Brain, ArrowRight, Trash2, Sparkles, Send, Command, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Header = () => {
  const { user, cart, mode, setMode, setCartOpen, setAiOpen, setCmdOpen, logout } = useApp();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(DEFAULT_CHROME.header);
  const navigate = useNavigate();
  const cartCount = cart.reduce((a, x) => a + x.qty, 0);

  const { pathname } = useLocation();
  useEffect(() => {
    api.get("/content/chrome").then(({ data }) => { if (data?.header) setCfg(mergeChrome(data).header); }).catch(() => {});
  }, []);

  const isActive = (p) => (p === "/" ? pathname === "/" : p && pathname.startsWith(p));
  const isAdmin = user && (user.role === "super_admin" || user.role === "admin" || user.role === "manager");
  const menu = cfg.menu || [];
  const flat = menu.flatMap((m) => [m, ...(m.children || [])]).filter((m) => m.to && m.label);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#05070d]/70 backdrop-blur-2xl border-b border-white/[0.06]" data-testid="header">
      <div className="px-4 sm:px-8 lg:px-12 h-[76px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-baseline gap-1.5 group shrink-0" data-testid="nav-logo">
          {cfg.logo_image
            ? <img src={media(cfg.logo_image)} alt={`${cfg.logo_text_1} ${cfg.logo_text_2}`} className="h-12 sm:h-14 w-auto max-w-[240px] object-contain" />
            : <>
                <span className="text-2xl font-semibold tracking-tight text-white">{cfg.logo_text_1}</span>
                <span className="text-2xl font-light tracking-tight text-white/90">{cfg.logo_text_2}</span>
              </>}
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {menu.map((item, i) => (
            item.children && item.children.length ? (
              <div key={i} className="relative group" data-testid={`nav-parent-${(item.label || "").toLowerCase()}`}>
                <button className="flex items-center gap-1 text-[15px] text-slate-200/90 hover:text-white transition-colors">
                  {item.label} <ChevronDown size={14} className="opacity-60 group-hover:rotate-180 transition-transform duration-300" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                  <div className="glass-strong rounded-2xl p-2 min-w-[220px] border border-white/10 shadow-2xl">
                    {item.children.map((ch, j) => (
                      <Link key={j} to={ch.to || "#"} data-testid={`nav-child-${(ch.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                        className="block px-4 py-2.5 rounded-xl text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
                        {ch.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={i} to={item.to || "#"} data-testid={`nav-${(item.label || "").toLowerCase()}`}
                className={`text-[15px] text-slate-200/90 hover:text-white transition-colors ${isActive(item.to) ? "text-white nav-dot" : ""}`}>
                {item.label}
              </Link>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {!isAdmin && (
                <Link to="/my-program" data-testid="nav-my-program"
                  className="px-5 py-2.5 rounded-full btn-ghost text-[11px] flex items-center gap-2">
                  My Program
                </Link>
              )}
              <Link to={isAdmin ? "/admin" : "/my-lab"} data-testid="nav-account"
                className="px-6 py-2.5 rounded-full btn-primary text-[11px] flex items-center gap-2">
                {isAdmin ? "Admin" : "My Lab"} <ArrowRight size={13} />
              </Link>
              <button data-testid="nav-logout" onClick={logout}
                className="text-[10px] font-mono text-slate-500 hover:text-rose-400">EXIT</button>
            </div>
          ) : (
            <Link to="/auth" data-testid="nav-login" className="hidden md:flex items-center gap-2 px-6 py-2.5 rounded-full btn-primary text-[11px]">
              Enter Higher You <ArrowRight size={13} />
            </Link>
          )}
          <button data-testid="cmd-k-trigger" onClick={() => setCmdOpen(true)} aria-label="Search"
            className="hidden md:flex w-10 h-10 rounded-full items-center justify-center border border-white/50 text-white hover:shadow-[0_0_18px_rgba(142,197,255,0.5)] transition-shadow">
            <Search size={16} />
          </button>
          <button data-testid="ask-cg-ai-trigger" onClick={() => setAiOpen(true)} aria-label="Ask CG Labz"
            className="w-10 h-10 rounded-full flex items-center justify-center border border-white/30 text-slate-200 hover:border-white hover:shadow-[0_0_18px_rgba(142,197,255,0.5)] transition-all">
            <Sparkles size={15} />
          </button>
          <button data-testid="cart-drawer-trigger" onClick={() => setCartOpen(true)} aria-label="Cart"
            className="relative w-10 h-10 rounded-full flex items-center justify-center border border-white/30 text-slate-200 hover:border-white hover:shadow-[0_0_18px_rgba(142,197,255,0.5)] transition-all">
            <ShoppingCart size={15} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#8EC5FF] text-black text-[10px] font-bold flex items-center justify-center">{cartCount}</span>
            )}
          </button>
          <button data-testid="mode-toggle-button" onClick={() => setMode(mode === "immersive" ? "quick" : "immersive")}
            className="hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 text-[10px] font-mono uppercase tracking-widest text-slate-300 hover:text-white transition-colors">
            <span className={`w-1.5 h-1.5 rounded-full ${mode === "immersive" ? "bg-[#8EC5FF] shadow-[0_0_8px_#8EC5FF]" : "bg-violet-400 shadow-[0_0_8px_#8A2BE2]"}`} />
            {mode === "immersive" ? "Immersive" : "Quick"}
          </button>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10 p-4 space-y-1">
          {flat.map((m, i) => (
            <Link key={i} to={m.to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-white/5 text-sm">{m.label}</Link>
          ))}
          {user ? <Link to="/my-lab" onClick={() => setOpen(false)} className="block px-3 py-2 text-cyan-300 text-sm">My Lab</Link> : <Link to="/auth" onClick={() => setOpen(false)} className="block px-3 py-2 text-cyan-300 text-sm">Sign In</Link>}
        </div>
      )}
    </header>
  );
};

export const ImpersonationBanner = () => {
  const { user, stopImpersonate } = useApp();
  const navigate = useNavigate();
  if (!user || !user._impersonated_by) return null;
  const back = async () => {
    try { await stopImpersonate(); toast.success("Returned to your admin session"); navigate("/admin"); }
    catch { toast.error("Could not return to admin"); }
  };
  return (
    <div data-testid="impersonation-banner"
      className="fixed top-[84px] left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 sm:gap-3 max-w-[92vw] px-3 sm:px-4 py-2 rounded-full bg-amber-500/15 border border-amber-400/40 backdrop-blur-xl shadow-[0_0_24px_rgba(245,158,11,0.25)]">
      <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
      <span className="text-[11px] sm:text-xs text-amber-100 font-mono truncate">
        Viewing as <b className="text-white">{user.name || user.email}</b>
      </span>
      <button data-testid="stop-impersonation-btn" onClick={back}
        className="text-[10px] font-mono uppercase tracking-widest text-amber-200 hover:text-white border border-amber-400/40 rounded-full px-3 py-1 transition-colors shrink-0 whitespace-nowrap">
        Return to admin
      </button>
    </div>
  );
};

export const Footer = () => {
  const [f, setF] = useState(DEFAULT_CHROME.footer);
  useEffect(() => {
    api.get("/content/chrome").then(({ data }) => { if (data?.footer) setF(mergeChrome(data).footer); }).catch(() => {});
  }, []);
  return (
    <footer className="border-t border-white/10 mt-20" data-testid="footer">
      <div className="laser-line" />
      <div className="px-4 sm:px-8 lg:px-12 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="text-2xl mb-1">
            {f.logo_image
              ? <img src={media(f.logo_image)} alt={`${f.logo_text_1} ${f.logo_text_2}`} className="h-16 sm:h-20 w-auto max-w-[280px] object-contain" />
              : <><span className="font-semibold">{f.logo_text_1}</span> <span className="font-light">{f.logo_text_2}</span></>}
          </div>
          <div className="side-words mb-4">{f.tagline}</div>
          <p className="text-sm text-slate-400 max-w-xs leading-relaxed">{f.description}</p>
          <p className="mt-4 text-[11px] font-mono text-slate-500 leading-relaxed">{f.disclaimer}</p>
        </div>
        {(f.columns || []).map((col, i) => (
          <div key={i}>
            <div className="mono-label mb-3">{col.heading}</div>
            <ul className="space-y-2">{(col.links || []).map((lnk, j) => <li key={j}><Link to={lnk.to || "#"} className="text-sm text-slate-400 hover:text-cyan-300 transition-colors">{lnk.label}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="px-4 sm:px-8 lg:px-12 py-5 border-t border-white/5 flex flex-wrap gap-4 items-center justify-between text-[11px] font-mono text-slate-600">
        <span>{f.bottom_left}</span>
        <span>{f.bottom_right}</span>
      </div>
    </footer>
  );
};

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, removeFromCart, updateQty } = useApp();
  const subtotal = cart.reduce((a, x) => a + x.variant.price * x.qty, 0);
  const tiers = [
    { min: 50, pct: 30 }, { min: 25, pct: 22 }, { min: 10, pct: 15 }, { min: 5, pct: 10 },
  ];
  const qty = cart.reduce((a, x) => a + x.qty, 0);
  const tier = tiers.find((t) => qty >= t.min) || { pct: 0 };
  const next = tiers.slice().reverse().find((t) => qty < t.min);
  const discount = subtotal * tier.pct / 100;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="bg-[#0F0F17] border-l border-white/10 w-full sm:max-w-md flex flex-col">
        <SheetHeader><SheetTitle className="font-serif text-white text-2xl">Lab Cart</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto mt-6 space-y-4">
          {cart.length === 0 && <p className="text-slate-500 text-sm">Your cart is empty.</p>}
          {cart.map((x) => (
            <div key={x.variant.id} className="glass rounded-2xl p-4 flex gap-4" data-testid={`cart-item-${x.variant.sku}`}>
              <div className="w-14 h-20 rounded-lg bg-gradient-to-b from-cyan-500/10 to-violet-500/10 border border-cyan-400/20 flex items-center justify-center shrink-0">
                <FlaskConical className="text-cyan-300" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{x.product.name}</div>
                <div className="text-xs font-mono text-cyan-300/80 mt-0.5">{x.variant.label || `${x.variant.mg}${x.variant.unit}`} · {x.variant.sku}</div>
                {x.variant.price_per_mg != null && <div className="text-[11px] font-mono text-slate-500 mt-0.5">${x.variant.price_per_mg}/mg</div>}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(x.variant.id, x.qty - 1)} className="w-6 h-6 rounded glass text-sm">−</button>
                    <span className="text-sm font-mono w-6 text-center">{x.qty}</span>
                    <button onClick={() => updateQty(x.variant.id, x.qty + 1)} className="w-6 h-6 rounded glass text-sm">+</button>
                  </div>
                  <div className="text-sm font-mono text-cyan-300">${(x.variant.price * x.qty).toFixed(2)}</div>
                </div>
              </div>
              <button onClick={() => removeFromCart(x.variant.id)} className="self-start text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div className="pt-4 border-t border-white/10 space-y-2">
            {next && <div className="text-[11px] font-mono text-violet-300">ADD {next.min - qty} MORE ELIGIBLE ITEMS TO REACH {next.pct}% BULK TIER.</div>}
            <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span></div>
            {tier.pct > 0 && <div className="flex justify-between text-sm text-emerald-400"><span>Bulk savings ({tier.pct}%)</span><span className="font-mono">−${discount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-base font-medium pt-1"><span>Estimated total</span><span className="font-mono text-cyan-300">${(subtotal - discount).toFixed(2)}</span></div>
            <Link to="/checkout" onClick={() => setCartOpen(false)} data-testid="cart-checkout-btn"
              className="block text-center w-full mt-2 py-3 rounded-full btn-primary text-sm font-medium tracking-wide">
              Proceed to Checkout
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const AIChat = () => {
  const { aiOpen, setAiOpen, user } = useApp();
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hello. I am Ask CG Labz — your scientific research assistant. Ask me about our peptide library, specifications, COAs or tools." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput(""); setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("cgl_token") ? { Authorization: `Bearer ${localStorage.getItem("cgl_token")}` } : {}) },
        body: JSON.stringify({ session_id: sessionId, text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      setMsgs((m) => [...m, { role: "assistant", text: "" }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            try {
              const j = JSON.parse(line.slice(6));
              if (j.delta) { acc += j.delta; setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: acc }]); }
            } catch {}
          }
        }
      }
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "I'm having trouble connecting. Please try again." }]);
    }
    setLoading(false);
  };

  return (
    <Sheet open={aiOpen} onOpenChange={setAiOpen}>
      <SheetContent side="right" className="bg-[#0F0F17] border-l border-white/10 w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b border-white/10">
          <SheetTitle className="font-serif text-white text-xl flex items-center gap-2">
            <Brain className="text-cyan-300" size={18} /> Ask CG Labz
          </SheetTitle>
          <p className="text-[11px] font-mono text-slate-500 uppercase tracking-widest mt-1">Research assistant · Not medical advice</p>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[85%] rounded-2xl p-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-cyan-500/15 border border-cyan-400/20" : "glass"}`}>
              {m.text}
            </div>
          ))}
          {loading && <div className="glass max-w-[60%] rounded-2xl p-3 text-xs text-slate-400 animate-pulse">Analyzing…</div>}
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask about peptides, COAs, research…" data-testid="ai-chat-input"
            className="bg-black/40 border-white/10 text-sm" />
          <Button onClick={send} disabled={loading} data-testid="ai-chat-send" className="btn-primary rounded-xl shrink-0"><Send size={15} /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const CommandPalette = () => {
  const { cmdOpen, setCmdOpen } = useApp();
  const [q, setQ] = useState("");
  const [peptides, setPeptides] = useState([]);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (cmdOpen) {
      api.get("/peptides").then(({ data }) => setPeptides(data)).catch(() => {});
      api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    }
  }, [cmdOpen]);

  const results = [
    ...peptides.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || p.aliases?.some((a) => a.toLowerCase().includes(q.toLowerCase()))).slice(0, 5).map((p) => ({ type: "Peptide", label: p.name, to: `/peptides/${p.slug}` })),
    ...products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 5).map((p) => ({ type: "Product", label: p.name, to: `/shop/${p.slug}` })),
    { type: "Experience", label: "Enter the VR Lab", to: "/vr" },
    { type: "Tool", label: "Precision Calculator", to: "/tools/calculator" },
    { type: "Tool", label: "Compare Peptides", to: "/tools/compare" },
    { type: "Tool", label: "Research Match", to: "/tools/match" },
    { type: "Library", label: "COA Library", to: "/coa" },
    { type: "Tool", label: "Bulk Basket Builder", to: "/bulk" },
    { type: "Account", label: "My Lab", to: "/my-lab" },
    { type: "Admin", label: "Admin Dashboard", to: "/admin" },
  ].filter((r) => !q || r.label.toLowerCase().includes(q.toLowerCase()));

  if (!cmdOpen) return null;
  return (
    <Dialog open={cmdOpen} onOpenChange={setCmdOpen}>
      <DialogContent className="bg-[#0F0F17] border-white/10 p-0 max-w-xl top-[20%] translate-y-0">
        <DialogTitle className="sr-only">CG Labz Command Center</DialogTitle>
        <div className="p-4 border-b border-white/10">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search peptides, products, tools…" data-testid="cmd-k-input"
            className="bg-transparent border-0 text-base placeholder:text-slate-500 focus-visible:ring-0" />
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {results.slice(0, 12).map((r, i) => (
            <button key={i} onClick={() => { navigate(r.to); setCmdOpen(false); setQ(""); }}
              data-testid={`cmd-result-${r.type.toLowerCase()}`}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl hover:bg-white/5 text-left transition-colors">
              <span className="text-sm">{r.label}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-400/70">{r.type}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
